(ns app.front.view.model-codes.selector
  (:require [app.domain.model-codes.model :as model-codes-model]
            [app.front.entities :as entities]
            [app.front.ui.action-buttons :as ui-action-buttons]
            [app.front.ui.section :as section]
            [app.front.ui.table :as ui-table]
            [app.front.util :as futil]
            [app.front.view.model-codes.common :as model-codes-common]
            [app.front.view.model-codes.util :as model-codes-util]
            [clojure.string :as str]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::data :-> ::data)


(rf/reg-event-fx ::init
  (fn [{:keys [db]} [_ group-id]]
    {:db       (-> db
                   (p/dissoc-in [::data :selected-section-path]))
     :dispatch [::model-codes-common/init group-id true]}))


(rf/reg-event-db ::toggle-section-row-expansion
  (fn [db [_ id]]
    (let [expanded? (-> db ::data :expanded-section-rows (contains? id))]
      (update-in db [::data :expanded-section-rows] (comp set (if expanded? disj conj)) id))))

(rf/reg-sub ::expanded-section-rows
  :<- [::data]
  (fn [data _]
    (:expanded-section-rows data #{})))

(rf/reg-sub ::selected-section-path
  :<- [::data]
  :-> :selected-section-path)

(rf/reg-sub ::selected-section-path-str
  :<- [::entities/context]
  :<- [::selected-section-path]
  (fn [[ctx section-path]]
    (p/if-all-let [path             (rest section-path)
                   entities         (group-by :db/id (entities/find-entities ctx (set path)))
                   ordered-entities (map #(->> %
                                               (get entities)
                                               first) path)]
      (->> ordered-entities
           (map :model-code-item/title)
           (str/join " / ")))))

(rf/reg-event-db ::select-section
  (fn [db [_ id]]
    (assoc-in db [::data :selected-section-path] id)))

(rf/reg-event-db ::clear-filtering
  (fn [db]
    (p/dissoc-in db [::data :selected-section-path])))

(rf/reg-sub ::filtering?
  :<- [::model-codes-common/search-text]
  :<- [::selected-section-path]
  (fn [[search-text selected-section-path] _]
    (or (not (util/nil-or-blank? search-text))
        selected-section-path)))

(rf/reg-sub ::recent-items
  :<- [::entities/context]
  (fn [ctx [_ group-id book-id]]
    (entities/find-entities ctx
                            (model-codes-util/recent-contents group-id book-id))))

(defn code-content [tr editor insert-text code-reference-fn {:model-code-item/keys [alt-content] :db/keys [id] :keys [content] :as item} group-id book-id]
  (let [code-reference (model-codes-model/model-code-item-label-with-ordinal item)]
    [:div.px-6.flex.flex-col.gap-4
     [:div
      [:div.bold.uppercase.text-xs.mb-2 (tr [:model-codes/original-text])]
      [editor {:id                 (str id "-content")
               :initial-html-value content}]]
     (when alt-content
       [:div
        [:div.bold.uppercase.text-xs.mb-2 (tr [:model-codes/customized-text])]
        [editor (merge {:id (str id "-alt-content")}
                       (if alt-content
                         {:initial-value alt-content}
                         {:initial-html-value content}))]])
     [ui-action-buttons/action-buttons
      {:size    :small
       :buttons (cond-> [{:text      (tr [:model-codes/add-code-reference])
                          :blip-text (tr [:model-codes/code-reference-added])
                          :kind      :tertiary
                          :on-click  (fn []
                                       (insert-text false (model-codes-model/model-code-item-label-with-ordinal item))
                                       (>evt [::model-codes-util/store-user-defaults group-id book-id id]))}
                         {:text      (tr [:model-codes/add-code-text])
                          :blip-text (tr [:model-codes/code-text-added])
                          :kind      :tertiary
                          :on-click  (fn []
                                       (insert-text true content)
                                       (>evt [::model-codes-util/store-user-defaults group-id book-id id])
                                       (when code-reference-fn (code-reference-fn code-reference)))}]
                  alt-content (conj {:text      (tr [:model-codes/add-customized])
                                     :blip-text (tr [:model-codes/customized-text-added])
                                     :kind      :tertiary
                                     :on-click  (fn []
                                                  (insert-text false alt-content)
                                                  (>evt [::model-codes-util/store-user-defaults group-id book-id id])
                                                  (when code-reference-fn (code-reference-fn code-reference)))}))}]]))

(defn- scroll-to-target [e]
  (some-> e
          .-target
          ui-table/get-parent-row
          futil/scroll-to-element))

(defn recent-table
  "Table listing recent code content items read from local storage."
  [{:keys [tr editor expanded-row-keys insert-text code-reference-fn]} group-id book-id]
  (let [items (<sub [::recent-items group-id book-id])]
    [:div
     [:div.mb-2.font-bold.text-lg (tr [:model-codes/recently-used] [(count items)])]
     [:div.border-t.border-solid.border-ui-gray.pt-2
      [ui-table/modern-responsive-table
       {:cols              [[:model-code-item/title
                             model-codes-model/model-code-item-label-with-ordinal]]
        :tr                tr
        :items             items
        :expanded-func     (fn [{:keys [content] :as item}]
                             (cond
                               content
                               [code-content tr editor insert-text code-reference-fn item group-id book-id]))
        :expanded-row-keys expanded-row-keys
        :on-expand         (fn [item e]
                             (when-not (contains? expanded-row-keys item)
                               (scroll-to-target e))
                             (>evt [::model-codes-common/toggle-row-expansion item]))}]]]))

(defn anchor-elem-to-scroll-id
  [prefix elem-path]
  (->> elem-path
       (str/join "-")
       (str prefix "-")))

(defn section-table
  "Table listing code sections for section filtering."
  [{:keys   [tr expanded-row-keys anchor-elem-id]
    main-id :id
    :as     ctx}
   parent-id-path
   selected-section-path]
  (let [items (<sub [::model-codes-common/item-children (last parent-id-path)])]
    [ui-table/modern-responsive-table
     {:cols              [[:model-code-item/title
                           (fn [{:db/keys [id] :as section}]
                             (cc/radio-button {:id         (str "section-" main-id "-" id)
                                               :name       (str "section-" main-id "-" id)
                                               :value      id
                                               :checked    (= id (last selected-section-path))
                                               :label-text (model-codes-model/model-code-item-label-with-ordinal section)
                                               :on-change  (fn []
                                                             (->> (conj parent-id-path id)
                                                                  (anchor-elem-to-scroll-id anchor-elem-id)
                                                                  futil/scroll-to-id)
                                                             (>evt [::select-section (conj parent-id-path id)]))}))
                           {:flex "none"}]]
      :tr                tr
      :items             items
      :expanded-func     (fn [{:db/keys [id]}]
                           (when (<sub [::model-codes-common/has-children-with-sections? id])
                             [:div.pl-6
                              [section-table
                               (update ctx :id str "-" id)
                               (conj parent-id-path id)
                               selected-section-path]]))
      :expanded-row-keys expanded-row-keys
      :on-expand         (fn [item e]
                           (when-not (contains? expanded-row-keys item)
                             (scroll-to-target e))
                           (>evt [::toggle-section-row-expansion item]))}]))


(defn passes-search-filter? [parent-id selected-section-path filtered-row-keys item]
  (let [path-set (set selected-section-path)]
    (and
      (or (empty? filtered-row-keys)
          (contains? filtered-row-keys (:db/id item)))
      (or (empty? selected-section-path)
          (contains? path-set (:db/id item))
          (contains? path-set parent-id)
          (not (model-codes-model/can-have-content-items? item))))))

(rf/reg-sub ::filtered-children
  (fn [[_ parent-id]]
    (rf/subscribe [::model-codes-common/item-children parent-id]))
  (fn [children [_ parent-id selected-section-path filtered-row-keys]]
    (->> children
         (filter (partial passes-search-filter?
                          parent-id
                          selected-section-path
                          filtered-row-keys)))))

(defn content-table
  "Table listing sections and the code contents."
  [{:keys [id tr editor filtered-row-keys expanded-row-keys insert-text code-reference-fn root? group-id book-id]
    :as   ctx}
   parent-id
   selected-section-path]
  (let [items      (<sub [::filtered-children parent-id selected-section-path filtered-row-keys])
        item-count (count items)]
    [:div
     (when root?
       [:div.mb-2.font-bold.text-lg
        (tr (if (not= item-count 1)
              [:model-codes/code-results]
              [:model-codes/code-result])
            [item-count])])
     [:div
      {:id id}
      [ui-table/modern-responsive-table
       {:cols              [[:model-code-item/title
                             model-codes-model/model-code-item-label-with-ordinal]]
        :tr                tr
        :items             items
        :expanded-func     (fn [{:keys [content] :db/keys [id] :as item}]
                             (cond
                               (<sub [::model-codes-common/has-children? id])
                               [:div.pl-6
                                [content-table (dissoc ctx :root?) id selected-section-path]]
                               content
                               [code-content tr editor insert-text code-reference-fn item group-id book-id]))
        :expanded-row-keys expanded-row-keys
        :on-expand         (fn [item e]
                             (when-not (contains? expanded-row-keys item)
                               (scroll-to-target e))
                             (>evt [::model-codes-common/toggle-row-expansion item]))}]]]))


(defn model-code-select [{:keys [id tr editor insert-text code-reference-fn group-id]}]
  (let [books                 (<sub [::model-codes-common/books group-id])
        selected-book         (<sub [::model-codes-common/selected-book])
        book-sections         (<sub [::model-codes-common/selected-book-sections])
        selected-section-path (<sub [::selected-section-path])
        section-path-str      (<sub [::selected-section-path-str])
        content-loading?      (<sub [:comm/command-processing? :model-codes/group-selections-for-book])
        filtering?            (<sub [::filtering?])]
    [:div
     [model-codes-common/model-code-search tr group-id books {:book-select-evt [::clear-filtering]}]

     (when selected-book
       (cond
         (seq book-sections)
         [:div
          [section/subsection-2
           {:tr                   tr
            :title                (if selected-section-path
                                    section-path-str
                                    (tr [:model-codes/filter]))
            :title-class          "text-base"
            :header-buttons       (when (<sub [::selected-section-path])
                                    [{:description (tr [:clear])
                                      :kind        :ghost
                                      :on-click    #(>evt [::select-section nil])}])
            :initially-collapsed? true}
           [section-table
            {:id                (str "filter-" id)
             :tr                tr
             :expanded-row-keys (<sub [::expanded-section-rows])
             :insert-text       insert-text
             :anchor-elem-id    id}
            [(:db/id selected-book)]
            selected-section-path]]
          (if filtering?
            [content-table
             {:id                (anchor-elem-to-scroll-id id selected-section-path)
              :tr                tr
              :editor            editor
              :filtered-row-keys (<sub [::model-codes-common/filtered-rows])
              :expanded-row-keys (<sub [::model-codes-common/expanded-rows])
              :insert-text       insert-text
              :code-reference-fn code-reference-fn
              :root?             true
              :group-id          group-id
              :book-id           (:db/id selected-book)}
             (or (last selected-section-path) (:db/id selected-book))
             selected-section-path]
            [recent-table
             {:tr                tr
              :editor            editor
              :expanded-row-keys (<sub [::model-codes-common/expanded-rows])
              :insert-text       insert-text
              :code-reference-fn code-reference-fn}
             group-id
             (:db/id selected-book)])]

         content-loading?
         (cc/loading-spinner {:with-overlay false})

         :else
         [:div (tr [:model-codes/no-book-content])]))]))
